import type { AssetTagging } from '@xphoto/shared';
import { fetchAssetTagging } from './tagging-api';

/**
 * PRÉCHARGEMENT du `GET /api/assets/:id/tagging` de la photo SUIVANTE (audit A5).
 *
 * Le Tagueur est une tâche LONGUE (réviser 200 photos d'affilée) et sa boucle était
 * entièrement séquentielle : « Valider et suivant » → POST → navigation → GET BLOQUANT →
 * « Chargement… ». Une latence serveur de 200 ms se payait donc à CHAQUE photo, en plein
 * milieu du geste. Ici on lance le GET de la photo suivante DÈS QUE la courante est
 * prête : à l'arrivée sur la photo suivante, la réponse est (le plus souvent) déjà là.
 *
 * PATRON : celui du préchargement directionnel de `viewer/use-viewer-navigation.ts` —
 * on anticipe UNIQUEMENT dans le sens de marche, et un échec de préchargement est
 * silencieux (le chargement normal du panneau reprendra la main et affichera l'erreur).
 *
 * SÉMANTIQUE « PRENDRE » (`takePrefetched`) : lire une entrée la RETIRE. Conséquences
 * voulues :
 *  - la réserve ne grossit jamais (au plus une photo d'avance) ;
 *  - aucune donnée PÉRIMÉE ne peut être servie : revenir en arrière (‹) sur une photo
 *    déjà taguée refait un vrai GET, qui reflète le commit qu'on vient d'y faire.
 */

/** Requêtes de préchargement en vol / abouties, par id d'asset. Au plus ~1 entrée. */
const prefetched = new Map<string, Promise<AssetTagging>>();

/**
 * Lance (si besoin) le chargement du tagging de `assetId` en arrière-plan. Idempotent :
 * re-demander un id déjà en réserve ne ré-émet rien. Le rejet est absorbé ici — il sera
 * re-produit au consommateur via la promesse rendue par `takePrefetched`.
 */
export function prefetchAssetTagging(assetId: string): void {
  if (prefetched.has(assetId)) return;
  const promise = fetchAssetTagging(assetId);
  // Sans ce `catch`, un préchargement en échec remonterait en « unhandled rejection »
  // alors que personne n'attend encore la promesse.
  promise.catch(() => {
    prefetched.delete(assetId);
  });
  prefetched.set(assetId, promise);
}

/**
 * Récupère ET RETIRE le préchargement de `assetId`, ou `undefined` s'il n'y en a pas
 * (première photo, préchargement en échec, retour en arrière).
 */
export function takePrefetchedTagging(assetId: string): Promise<AssetTagging> | undefined {
  const promise = prefetched.get(assetId);
  if (promise !== undefined) prefetched.delete(assetId);
  return promise;
}

/** Vide la réserve (fermeture du Tagueur, tests). */
export function clearPrefetchedTagging(): void {
  prefetched.clear();
}
